import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, Zap, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * MeterDataImportDialog — upload a MeterOnline HH-profile CSV/XLSX export for
 * one stream (generation / import / export) of a site and write it into the
 * 15-min monitoring history. Dry-run preview → confirm. Fills gaps by default;
 * admins can tick overwrite. Mirrors the daily sync's HH→15-min split.
 */
const STREAMS = [
  { key: "generation", label: "Generation", icon: Zap,
    hint: "Solar generation meter (a MeterOnline \u201cForward\u201d profile)." },
  { key: "import", label: "Grid import", icon: ArrowDownToLine,
    hint: "Energy drawn from the grid (import \u201cForward\u201d profile)." },
  { key: "export", label: "Grid export", icon: ArrowUpFromLine,
    hint: "Energy exported to the grid (export \u201cReverse\u201d profile)." },
];

export default function MeterDataImportDialog({ projectId, onClose, onImported }) {
  const [stream, setStream] = useState("generation");
  const [file, setFile] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const runPreview = async (f, strm = stream, ow = overwrite) => {
    if (!f) return;
    setBusy(true); setCommitted(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("stream", strm);
      fd.append("overwrite", ow ? "true" : "false");
      fd.append("dry_run", "true");
      const r = await api.post(`/monitoring/${projectId}/meter-data/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      if (!r.data.ok) toast.error((r.data.errors || []).join(" · ") || "Could not read the file");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
      setPreview(null);
    } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setCommitted(null);
    runPreview(f);
  };

  const pickStream = (k) => {
    setStream(k); setPreview(null); setCommitted(null);
    if (file) runPreview(file, k, overwrite);
  };

  const toggleOverwrite = (checked) => {
    setOverwrite(checked);
    if (file) runPreview(file, stream, checked);
  };

  const doCommit = async () => {
    if (!file || !preview?.ok || preview.will_fill === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("stream", stream);
      fd.append("overwrite", overwrite ? "true" : "false");
      fd.append("dry_run", "false");
      const r = await api.post(`/monitoring/${projectId}/meter-data/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCommitted(r.data);
      toast.success(`Imported ${r.data.written} quarter(s)${r.data.skipped ? ` · ${r.data.skipped} skipped` : ""}`);
      onImported && onImported();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         onClick={onClose} data-testid="meter-data-import-dialog">
      <div className="bg-white w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--line)]">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={16} />
            <div className="font-semibold">Upload meter data (MeterOnline export)</div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="meter-data-close"><X size={14} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          <div>
            <div className="label mb-1.5">1. Which meter stream is this file?</div>
            <div className="grid grid-cols-3 gap-2" data-testid="meter-data-stream-toggle">
              {STREAMS.map(s => {
                const Icon = s.icon;
                return (
                  <button key={s.key} onClick={() => pickStream(s.key)}
                          data-testid={`meter-data-stream-${s.key}`}
                          className={`flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-widest border ${stream === s.key ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] muted"}`}>
                    <Icon size={13} /> {s.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] muted mt-1.5">{STREAMS.find(s => s.key === stream)?.hint}</div>
          </div>

          <div>
            <div className="label mb-1.5">2. Upload the MeterOnline HH-profile file</div>
            <input ref={inputRef} type="file" accept=".csv,.txt,.xlsx,.xlsm" className="hidden"
                   onChange={onFile} data-testid="meter-data-file-input" />
            <div className="flex items-center gap-3 flex-wrap">
              <button className="btn-ghost inline-flex items-center gap-1.5" disabled={busy}
                      onClick={() => inputRef.current?.click()} data-testid="meter-data-pick-file">
                <Upload size={12} /> {file ? "Choose different file…" : "Choose CSV / Excel"}
              </button>
              {file && <span className="text-sm muted truncate max-w-[220px]">{file.name}</span>}
              {busy && <Loader2 size={14} className="animate-spin" />}
            </div>
            <label className="flex items-center gap-2 text-xs mt-2 cursor-pointer"
                   title="Replace readings that already exist for this stream — not just fill gaps">
              <input type="checkbox" checked={overwrite}
                     onChange={(e) => toggleOverwrite(e.target.checked)}
                     data-testid="meter-data-overwrite" />
              Overwrite existing readings (not just gaps)
            </label>
          </div>

          {preview?.ok && (
            <div className="p-3 border border-[var(--line)] bg-[var(--surface-2)]" data-testid="meter-data-preview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Stat label="Readings" value={preview.parsed_readings.toLocaleString()} testid="meter-data-parsed" />
                <Stat label="15-min slots" value={preview.quarters.toLocaleString()} />
                <Stat label="Total kWh" value={preview.total_kwh?.toLocaleString()} />
                <Stat label={overwrite ? "Will overwrite" : "Will fill"} value={preview.will_fill.toLocaleString()} tone="accent" testid="meter-data-will-fill" />
              </div>
              <div className="text-[11px] font-mono muted mt-2">
                {preview.date_from} → {preview.date_to}
                {preview.will_skip > 0 && <span> · {preview.will_skip.toLocaleString()} already present (skipped)</span>}
              </div>
              {preview.sample?.length > 0 && (
                <div className="text-[11px] font-mono muted mt-1">
                  e.g. {preview.sample.slice(0, 3).map(s => `${s.ts}=${s.kwh}`).join("  ")}
                </div>
              )}
              {preview.errors?.length > 0 && (
                <div className="text-[11px] text-amber-700 mt-2 flex items-start gap-1">
                  <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {preview.errors.join(" · ")}
                </div>
              )}
            </div>
          )}

          {preview && !preview.ok && (
            <div className="p-3 border border-red-200 bg-red-50 text-xs text-red-700" data-testid="meter-data-error">
              <AlertTriangle size={12} className="inline mr-1" />
              {(preview.errors || ["Could not read any readings from this file"]).join(" · ")}
            </div>
          )}

          {committed && (
            <div className="p-3 border border-[#17663C] bg-[#DBF3E4] text-sm" data-testid="meter-data-commit-result">
              <CheckCircle2 size={12} className="inline mr-1 text-[#17663C]" />
              Imported {committed.written?.toLocaleString()} 15-min slot(s)
              {committed.skipped ? ` · ${committed.skipped.toLocaleString()} skipped (already present)` : ""}.
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[var(--line)] flex items-center justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} data-testid="meter-data-cancel">{committed ? "Done" : "Cancel"}</button>
          {!committed && (
            <button className="btn-primary inline-flex items-center gap-1.5" onClick={doCommit}
                    disabled={!preview?.ok || preview.will_fill === 0 || busy} data-testid="meter-data-commit">
              <Upload size={12} />
              {preview?.ok ? `Import ${preview.will_fill.toLocaleString()} slot(s)` : "Import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label">{label}</div>
      <div className={`font-display text-lg font-bold mt-0.5 ${tone === "accent" ? "text-[var(--accent)]" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
